import React from "react";
import styled from "styled-components";
import ReleaseDetailsForm from "./ReleaseDetailsForm";
import PlatFormArtistCard from "./PlatFormArtistCard";
import YungLeanStarzCover from "assets/YungLeanStarzCover.png";
import { useNavigate, useParams } from "react-router";
import { PlatFormUserInfoContentProps } from "../types";
import {FormattedMessage} from "react-intl";

const UserInfoContent: React.FC<PlatFormUserInfoContentProps> = ({
  link = "",
  releases,
}) => {
  const navigate = useNavigate();

  const releaseData = releases;
  const coverUrl = releaseData?.cover_url || YungLeanStarzCover;
  const artists: any[] = releaseData?.artists || [];
  const {id} = useParams();
  const handleRedirect = () => {
    navigate(`/releases/new-release/${id}?step=upload-image`);
  };

  const platforms: any[] = releaseData?.platforms || [];
  const countries: any[] = releaseData?.countries || [];
  const platformsText = platforms?.length ? `${platforms.length} platforms` : "—";
  const countriesText = "The Whole World";
  const releaseTypeText = (() => {
    const raw = String(releaseData?.release_type ?? "").toUpperCase();
    if (raw === "EP") return "EP";
    if (raw === "SINGLE") return "Single";
    if (raw === "ALBUM") return "Album";
    return raw ? raw.replace(/_/g, " ") : "Release";
  })();
  const tracksCount = Array.isArray(releaseData?.tracks) ? releaseData.tracks.length : 0;
  const truncateText = (value: any, max = 20) => {
    const s = String(value ?? "");
    if (s.length <= max) return s;
    return `${s.slice(0, max)}...`;
  };
  const titleText = truncateText(releaseData?.title || "—", 20);
  const versionText = truncateText(releaseData?.version || "", 20);

  return (
    <UserInfoContentBox>
      <div>
        <ImgContent>
          <PreviewImage src={coverUrl} />
          <EditButton onClick={(e) => { e.stopPropagation(); handleRedirect() }}>
            <FormattedMessage
              id="ReleaseDetails.UserInfoContent.Edit"
              defaultMessage="Edit"
            />
          </EditButton>
        </ImgContent>
        <UserInfoBox>
          <StatusBox>
            <FormattedMessage
              id="ReleaseDetails.UserInfoContent.StatusReleased"
              defaultMessage="Status: "
            />
            {String(releaseData?.status || "—")}
          </StatusBox>
          <UserInfo>
            <UserName>
              {titleText}
            </UserName>
            <UserData>{versionText}</UserData>
          </UserInfo>

          <ReleaseInfo>
            <ReleaseDetailWrapper>
              <ReleaseDetails>
                {`${releaseTypeText}, ${tracksCount} tracks`}
              </ReleaseDetails>
            </ReleaseDetailWrapper>
            <ReleaseDetailWrapperPlat>
              <ReleaseDetails>
                {platformsText}
              </ReleaseDetails>
              {platforms?.length ? (
                <TooltipBox>
                  {platforms.map(p => p?.name ?? p).filter(Boolean).join(", ")}
                </TooltipBox>
              ) : null}
            </ReleaseDetailWrapperPlat>

            <ReleaseDetailWrapper>
              <ReleaseDetails>
                {countriesText}
              </ReleaseDetails>
            </ReleaseDetailWrapper>

            <ReleaseDetailWrapper>
              <ReleaseDetails>
                <FormattedMessage
                  id="ReleaseDetails.UserInfoContent.EstimatedRelease"
                  defaultMessage="Estimated release {date}"
                  values={{date: releaseData?.release_date || releaseData?.created_at || "—"}}
                />
              </ReleaseDetails>
            </ReleaseDetailWrapper>
          </ReleaseInfo>
        </UserInfoBox>
      </div>

      <ArtistsBox>
        <ArtistsTitle>
          <FormattedMessage
            id="ReleaseDetails.UserInfoContent.Artists"
            defaultMessage="Artists"
          />
        </ArtistsTitle>
        <MusicType>
          {artists?.map((artist, index) => (
            <span key={index}>
              {artist.name}
              {artists.length - 1 !== index ? ", " : ""}
            </span>
          ))}
        </MusicType>

        <ArtistsGrid>
          {artists?.map((artist) => (
            <PlatFormArtistCard
              key={artist.id}
              name={artist.name}
              isPrimary={artist.role ? artist.role === "MAIN" : false}
              onTogglePrimary={() =>
                console.log(`Toggled primary for artist ${artist.id}`)
              }
              onDelete={() => console.log(`Deleted artist ${artist.id}`)}
            />
          ))}
        </ArtistsGrid>

        <ReleaseDetailsForm
          title={releaseData?.title}
          version={releaseData?.version}
          label={releaseData?.label}
          release_date={releaseData?.release_date}
          original_release_date={releaseData?.original_release_date}
          genres={releaseData?.genres || []}
          pline_value={releaseData?.pline_value}
          cline_value={releaseData?.cline_value}
        />
      </ArtistsBox>
    </UserInfoContentBox>
  );
};

const UserInfoContentBox = styled.div`
  width: 100%;
  display: flex;
  gap: 50px;

  /* Mobile: match NewRelease Review — cover on top, everything below */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 18px;
  }
`;

const PreviewImage = styled.img`
  max-width: 300px;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 16px;
  }
`;

const UserInfoBox = styled.div`
  margin-top: 13px;
  display: flex;
  flex-direction: column;

`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.p`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  color: #000000;
  margin: 0;
`;

const UserData = styled.p`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  color: #00000080;
  margin: 0;
`;

const ReleaseInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;

  @media (max-width: 768px) {
    padding-top: 16px;
    gap: 1px;
  }
`;

const ReleaseDetailWrapper = styled.div`
  position: relative; 
`;

const ReleaseDetailWrapperPlat = styled.div`
  position: relative;
  display: inline-block;

  /* On hover, only show tooltip; keep cursor default and text without underline */
  &:hover div {
      display: block;
}
`;
const ImgContent = styled.div`
  position: relative;
  &:hover button {
    display: block;
    cursor:pointer
  }
`;

const ReleaseDetails = styled.p`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
  margin-bottom: 0;
  cursor: default;
  border-bottom: 1px solid #fffff;
`;

const TooltipBox = styled.div`
  display: none;
  position: absolute;
  bottom: 20px;
  left: 0;
  width: fit-content;
  max-width: min(520px, calc(100vw - 40px));
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  background-color:#E2E5FF;
  border-radius: 15px;
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: 0;
  padding: 17px 14px 15px 14px;

  @media (max-width: 768px) {
    width: fit-content;
    max-width: calc(100vw - 40px);
    left: 0;
  }
`;

const EditButton = styled.button`
  display:none;
  width: 51px;
  height: 24px;
  border-radius: 100px;
  border:none;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0;
  position: absolute;
  right:18px;
  bottom:18px;
`;

const ArtistsBox = styled.div`
  margin-top: 1px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const ArtistsTitle = styled.span`
  font-family: SF Pro Display;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
`;

const MusicType = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
`;

const ArtistsGrid = styled.div`
  width: max-content;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
  margin-top: 10px;

  @media (max-width: 768px) {
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;
const StatusBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 98px;
  height: 20px;
  border-radius: 4px;
  background: #87FF9B;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #000000;


`
export default UserInfoContent;

